import React, { useEffect, useState } from 'react';
import { ResearchReport } from '../../services/researchReportService';
import { markdownToHtml } from '../../utils/markdownUtils';
import LoadingSpinner from '../ui/LoadingSpinner';

interface ResearchReportModalProps {
  report: ResearchReport | null;
  loading?: boolean;
  onClose: () => void;
}

const ResearchReportModal: React.FC<ResearchReportModalProps> = ({ report, loading, onClose }) => {
  const [htmlContent, setHtmlContent] = useState<string>('');

  useEffect(() => {
    if (report?.content) {
      setHtmlContent(markdownToHtml(report.content));
    }
  }, [report]);

  if (!report && !loading) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-fade-in">
      <div className="bg-slate-900/90 border border-white/10 rounded-2xl w-full max-w-4xl h-[85vh] flex flex-col shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-white/10 bg-white/5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-xl shadow-lg shadow-blue-500/20">
              🤖
            </div>
            <div>
              <h2 className="text-xl font-bold text-white leading-tight">
                {loading ? '研报加载中...' : report?.title}
              </h2>
              {!loading && report && (
                <p className="text-white/40 text-xs mt-1">
                  生成时间: {new Date(report.timestamp).toLocaleString()}
                </p>
              )}
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2.5 hover:bg-white/10 rounded-xl text-white/50 hover:text-white transition-all border border-transparent hover:border-white/10"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 md:p-8 custom-scrollbar">
          {loading ? (
            <div className="flex flex-col items-center justify-center h-full gap-4">
              <LoadingSpinner />
              <p className="text-white/40 animate-pulse text-sm">正在深度解析市场数据...</p>
            </div>
          ) : (
            <article className="prose prose-invert prose-sm md:prose-base max-w-none prose-headings:text-blue-400 prose-a:text-cyan-400 prose-strong:text-white/90 prose-code:text-pink-300">
              <div dangerouslySetInnerHTML={{ __html: htmlContent }} />
            </article>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-white/10 bg-white/5 flex justify-between items-center text-[10px] text-white/30 italic">
          <span>* 本报告由 AI 自动生成，仅供参考，不构成投资建议。</span>
          <button
            onClick={onClose}
            className="px-6 py-2 bg-white/5 hover:bg-white/10 border border-white/10 rounded-lg text-white/70 text-xs transition-all"
          >
            关闭
          </button>
        </div>
      </div>
    </div>
  );
};

export default ResearchReportModal;
